/**
 * Sessions API module for fetching and managing chat sessions.
 */

import type {
  SessionListResponse,
  SessionMessagesResponse,
} from "../types/api";
import { materiomaiApiClient, ApiError } from "./client";

/**
 * Fetches the list of saved chat sessions for the authenticated user.
 * @returns {Promise<SessionListResponse>} The list of session summaries.
 * @throws {ApiError} If the request fails.
 */
export const fetchSessions = async (): Promise<SessionListResponse> => {
  try {
    return await materiomaiApiClient.get<SessionListResponse>(
      "/api/v1/sessions"
    );
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(
      "Failed to fetch sessions",
      undefined,
      "SESSIONS_FETCH_ERROR"
    );
  }
};

/**
 * Fetches messages for a specific chat session.
 * @param {string} sessionId - The session ID to fetch messages for.
 * @returns {Promise<SessionMessagesResponse>} The session messages.
 * @throws {ApiError} If the request fails.
 */
export const fetchSessionMessages = async (
  sessionId: string
): Promise<SessionMessagesResponse> => {
  try {
    return await materiomaiApiClient.get<SessionMessagesResponse>(
      `/api/v1/sessions/${sessionId}/messages`
    );
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(
      "Failed to fetch session messages",
      undefined,
      "SESSION_MESSAGES_FETCH_ERROR"
    );
  }
};

/**
 * Deletes a specific chat session.
 * @param {string} sessionId - The session ID to delete.
 * @throws {ApiError} If the request fails.
 */
export const deleteSession = async (sessionId: string): Promise<void> => {
  try {
    await materiomaiApiClient.delete(`/api/v1/sessions/${sessionId}`);
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(
      "Failed to delete session",
      undefined,
      "SESSION_DELETE_ERROR"
    );
  }
};
